import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { getCoach, refreshCoach } from '@/data/client';
import type { CoachRef, CoachResponse, DealCoach, DraftKind } from '@/data/types';
import { SourceViewer } from '@/components/vault/SourceViewer';

const TEMP: Record<DealCoach['temperature'], string> = { hot: 'Hot', warm: 'Warm', cool: 'Cooling', cold: 'Cold' };
const TONE: Record<DealCoach['tone']['current'], string> = { positive: 'Positive', neutral: 'Neutral', hesitant: 'Hesitant', negative: 'Negative' };
const TREND: Record<DealCoach['tone']['trend'], string> = { improving: '↑ improving', steady: '→ steady', cooling: '↓ cooling', unclear: '' };
const CHIP_ICON: Record<CoachRef['type'], string> = { email: 'mail', vault: 'file-text', activity: 'edit', deal: 'dollar' };

/* The deal coach: where the deal stands from the latest conversations, the
   objections (open and answered), and the next step for this stage — each point
   linked to the email, transcript or note it came from. It re-reads the deal by
   itself when a new conversation arrives. */
export function CoachCard({ dealId, onDraft }: { dealId: string; onDraft: (kind: DraftKind, instructions?: string) => void }) {
  const qc = useQueryClient();
  const [viewing, setViewing] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['coach', dealId], queryFn: () => getCoach(dealId), refetchInterval: (q) => (q.state.data?.updating ? 5000 : false) });
  const refresh = useMutation({ mutationFn: () => refreshCoach(dealId), onSuccess: (r) => qc.setQueryData(['coach', dealId], r) });
  if (!data) return null;
  if (data.state === 'empty') {
    return <div className="tr-card tr-card--dark snap snap--empty"><Head data={data} /><p>The coach starts once this deal has a conversation: captured email, a transcript, or a note.</p></div>;
  }
  if (data.state === 'unavailable') return <div className="tr-card tr-card--dark snap snap--empty"><Head data={data} /><p>The coach needs the Anthropic API key on the server.</p></div>;
  if (!data.coach) return <div className="tr-card tr-card--dark snap snap--empty"><Head data={data} /><p>Reading the latest conversations… about half a minute.</p></div>;

  const c = data.coach;
  const chips = (ids: string[]) => ids.length > 0 && (
    <span className="snap__chips">
      {ids.map((id) => {
        const ref = data.refs[id];
        if (!ref) return null;
        const open = ref.type === 'vault' || ref.type === 'email' ? () => setViewing(ref.id) : null;
        return (
          <button key={id} type="button" className={`snap__chip snap__chip--${ref.type === 'email' ? 'vault' : ref.type}`} title={ref.label} onClick={open ?? undefined} disabled={!open}>
            <Icon name={CHIP_ICON[ref.type]} size={10} />{ref.label.length > 28 ? `${ref.label.slice(0, 27)}…` : ref.label}
          </button>
        );
      })}
    </span>
  );
  const open = c.objections.filter((o) => o.status === 'open');
  const answered = c.objections.filter((o) => o.status === 'answered');

  return (
    <div className="tr-card tr-card--dark snap coach">
      <Head data={data} onRefresh={() => refresh.mutate()} refreshing={refresh.isPending} />
      <div className="coach__pills">
        <span className={`coach__pill coach__pill--${c.temperature}`}>{TEMP[c.temperature]}</span>
        <span className="coach__pill">Tone: {TONE[c.tone.current]} {TREND[c.tone.trend]}</span>
      </div>
      <p className="snap__rel">{c.summary}</p>
      {c.nextStep.text && (
        <div className="snap__next">
          <Icon name="arrow-right" size={16} />
          <div>
            <strong>{c.nextStep.text}</strong>
            <span>{c.nextStep.when ? `${c.nextStep.when} · ` : ''}{c.nextStep.why}</span>
            {chips(c.nextStep.sources)}
            {c.nextStep.channel === 'email' && (
              <div style={{ marginTop: 8 }}><Button size="sm" variant="outline" iconLeft={<Icon name="sparkles" size={13} />}
                onClick={() => onDraft('follow_up', `Do this: ${c.nextStep.text}`)}>Draft this email</Button></div>
            )}
          </div>
        </div>
      )}
      <div className="snap__grid">
        <div className="snap__sect">
          <h4>Tone</h4>
          <ul><li><span>{c.tone.why}</span>{chips(c.tone.sources)}</li></ul>
        </div>
        {open.length > 0 && (
          <div className="snap__sect">
            <h4>Open objections</h4>
            <ul>{open.map((o, i) => <li key={i}><span><em>{o.text}{o.raised ? ` (${o.raised})` : ''} — </em>{o.response}</span>{chips(o.sources)}</li>)}</ul>
          </div>
        )}
        {answered.length > 0 && (
          <div className="snap__sect">
            <h4>Answered</h4>
            <ul>{answered.map((o, i) => <li key={i}><span><em>{o.text}: </em>{o.response}</span>{chips(o.sources)}</li>)}</ul>
          </div>
        )}
        {c.buyingSignals.length > 0 && <div className="snap__sect"><h4>Buying signals</h4><ul>{c.buyingSignals.map((x, i) => <li key={i}><span>{x.text}</span>{chips(x.sources)}</li>)}</ul></div>}
        {c.risks.length > 0 && <div className="snap__sect"><h4>Risks</h4><ul>{c.risks.map((x, i) => <li key={i}><span>{x.text}</span>{chips(x.sources)}</li>)}</ul></div>}
        {c.talkingPoints.length > 0 && <div className="snap__sect"><h4>For the next conversation</h4><ul>{c.talkingPoints.map((t, i) => <li key={i}><span>{t}</span></li>)}</ul></div>}
      </div>
      {viewing && <SourceViewer sourceId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function Head({ data, onRefresh, refreshing }: { data: CoachResponse; onRefresh?: () => void; refreshing?: boolean }) {
  return (
    <div className="dash-ai__head snap__head">
      <span className="dash-ai__icon"><Icon name="target" size={18} /></span>
      <h2>Deal coach</h2>
      <span className="snap__meta">
        {data.updating ? 'Reading the latest conversations…' : data.generatedAt ? `Updated ${relativeTime(data.generatedAt)}` : ''}
        {data.error && !data.updating ? ' · last update failed' : ''}
      </span>
      {onRefresh && <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing || data.updating} iconLeft={<Icon name="refresh-cw" size={13} />}>Refresh</Button>}
    </div>
  );
}
